import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  useMediaQuery,
  IconButton
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import useData from "../../../Hooks/useData";
import { printUserBadge } from "../../admin/forms/UserBadgePrint";
import styles from './EventCheckinDetail.module.css';

const EventCheckinDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useData();
  const token = auth?.token;

  const [eventData, setEventData] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // For the input field
  const [searchTerm, setSearchTerm] = useState(''); // For actual search execution
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [totalCount, setTotalCount] = useState(0);
  const [checkedInCount, setCheckedInCount] = useState(0);

  const formatApiError = (e) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (data?.error) return data.error;
    if (typeof data === 'string') return `${status ? `${status}: ` : ''}${data}`.trim();
    if (data) return `${status ? `${status}: ` : ''}${JSON.stringify(data)}`.trim();
    return e?.message || 'Unknown error';
  };

  const fetchEventAndAttendees = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      // Fetch event details
      const eventResp = await axiosPrivate.get(`/api/events/${eventId}`);
      setEventData(eventResp.data);

      // Fetch attendees for this event
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const attendeesResp = await axiosPrivate.get(`/api/attendees?eventId=${eventId}&page=${page}&limit=${limit}&search=${searchTerm}`);
      
      const attendeesList = attendeesResp?.data?.attendees || [];
      const pagination = attendeesResp?.data?.pagination || {};
      
      setAttendees(attendeesList);
      setTotalCount(pagination.totalCount || 0);
      setCheckedInCount(pagination.checkedInCount || 0);
      
    } catch (e) {
      setError(true);
      enqueueSnackbar(formatApiError(e) || 'Failed to load event data', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, eventId, enqueueSnackbar, paginationModel.page, paginationModel.pageSize, searchTerm]);

  useEffect(() => {
    fetchEventAndAttendees();
  }, [fetchEventAndAttendees]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleCheckIn = useCallback(async (attendeeId, isCheckedIn) => {
    try {
      if (isCheckedIn) {
        // Undo check-in
        await axiosPrivate.put(`/api/attendees/${attendeeId}/checkout`);
        enqueueSnackbar('Attendee checked out successfully', { variant: 'success' });
      } else {
        // Check-in
        await axiosPrivate.put(`/api/attendees/${attendeeId}/checkin`);
        enqueueSnackbar('Attendee checked in successfully', { variant: 'success' });
      }
      fetchEventAndAttendees();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || 'Failed to update check-in status', { variant: 'error' });
    }
  }, [axiosPrivate, enqueueSnackbar, fetchEventAndAttendees]);

  const handleNavigateToUserCheckin = useCallback((userId) => {
    if (!userId) {
      enqueueSnackbar('User ID not available for this attendee', { variant: 'warning' });
      return;
    }
    navigate(`/admin/checkin/${eventId}/${userId}`);
  }, [navigate, eventId, enqueueSnackbar]);

  const handlePrintNameTag = useCallback((attendeeId) => {
    enqueueSnackbar('Name tag printing coming soon!', { variant: 'info' });
  }, [enqueueSnackbar]);

  const handlePrintBadge = useCallback((userId) => {
    if (!userId) {
      enqueueSnackbar('User ID not available for this attendee', { variant: 'warning' });
      return;
    }
    if (!token) {
      enqueueSnackbar('Authentication token not available', { variant: 'error' });
      return;
    }
    printUserBadge(userId, token);
  }, [token, enqueueSnackbar]);

  const handlePageChange = (event, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage - 1 }));
  };

  const handleSearchSubmit = useCallback(() => {
    // Update searchTerm which will trigger data fetch
    setSearchTerm(searchInput);
  }, [searchInput]);

  const handleSearchKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);

  const columns = useMemo(() => [
    {
      field: 'attendeeName',
      headerName: 'Attendee',
      minWidth: 160,
      flex: 1.1,
      renderCell: (params) => (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '16px !important', lineHeight: '1.3 !important' }}>
            {params.row.BPAttendeeFN} {params.row.BPAttendeeLN}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '15px !important', lineHeight: '1.3 !important' }}>
            {params.row.BPAttendeeUserID || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'BPAttendeeType',
      headerName: 'Registration',
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.value || 'General'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 380,
      flex: 2,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {params.row.BPAttendeeCheckInDate ? (
            <Button
              variant="contained"
              className={styles.checkedInButton}
              onClick={() => handleNavigateToUserCheckin(params.row.BPAttendeeUserID)}
            >
              Checked-In
            </Button>
          ) : (
            <Button
              variant="contained"
              className={styles.checkInButton}
              onClick={() => handleNavigateToUserCheckin(params.row.BPAttendeeUserID)}
            >
              Check-In
            </Button>
          )}
          {/* Print Name Tag removed */}
          <Button
            variant="contained"
            className={styles.printBadgeButton}
            onClick={() => handlePrintBadge(params.row.BPAttendeeUserID)}
          >
            Print Badge
          </Button>
        </Box>
      )
    }
  ], [handleNavigateToUserCheckin, handlePrintBadge]);

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Event Check-In</h1>
                <p>Check attendees in as they arrive and track attendance in real time</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.loadingContainer}>
            <CircularProgress />
            <Typography className={styles.loadingText}>Loading event data...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  if (error || !eventData) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Event Check-In</h1>
                <p>Check attendees in as they arrive and track attendance in real time</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.errorContainer}>
            <Typography color="error">Error occurred while loading event data</Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/admin/checkin')}
              startIcon={<ArrowBackIcon />}
              sx={{ mt: 2 }}
            >
              Back to Events
            </Button>
          </Box>
        </div>
      </main>
    );
  }

  const progressPercentage = totalCount > 0 ? (checkedInCount / totalCount) * 100 : 0;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = Math.min(startIndex + paginationModel.pageSize, totalCount);

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Event Check-In</h1>
              <p>Check attendees in as they arrive and track attendance in real time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        <div className="">
          <Box className={styles.container}>
            {/* Back Button */}
            <Box className={styles.backButtonContainer}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/checkin')}
                startIcon={<ArrowBackIcon />}
                className={styles.backButton}
              >
                Back to Events
              </Button>
            </Box>

            {/* Event Title and Summary */}
            <Paper className={styles.summaryPaper}>
              <Typography 
                variant="h5" 
                className={styles.eventTitle}
                sx={{
                  fontFamily: 'proxima-nova, sans-serif !important',
                  fontSize: '26px !important',
                  lineHeight: '24px !important',
                  fontWeight: '700 !important',
                  margin: '0 0 15px 0 !important',
                  letterSpacing: 'normal !important'
                }}
              >
                Check-In for {eventData.BP_Title}
              </Typography>
              
              <Typography 
                variant="body1" 
                className={styles.summaryText}
                sx={{
                  fontSize: '15px !important',
                  lineHeight: 'normal !important',
                  fontWeight: 'normal !important',
                  margin: '0 0 15px 0 !important',
                }}
              >
                {checkedInCount} attendees have been checked in out of {totalCount} total attendees
              </Typography>

              <Box className={styles.progressContainer}>
                <div className={styles.progressTrack}>
                  <div 
                    className={styles.progressFill} 
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </Box>
            </Paper>

            {/* Search Field */}
            <Paper className={styles.searchPaper}>
              <TextField
                className={styles.searchField}
                label="Search Attendee"
                placeholder="Search by name or volunteer number"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSearchSubmit}
                        edge="end"
                        aria-label="search"
                        color="primary"
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </Paper>

            {/* Attendee Data Table */}
            <Paper className={styles.dataGridPaper}>
              <DataGrid
                className="eventCheckinDataGrid"
                rows={attendees}
                columns={columns}
                getRowId={(row) => row.BPAttendeeID}
                disableRowSelectionOnClick
                autoHeight
                hideFooterPagination
                rowHeight={75}
              />
            </Paper>

            {/* Custom Pagination */}
            <Paper className={styles.paginationPaper}>
              <Stack
                className={styles.paginationStack}
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ width: '100%' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount > 0 ? startIndex + 1 : 0} to{' '}
                  {endIndex} of {totalCount} Attendees
                </Typography>

                <Stack className={styles.paginationControls} direction="row" alignItems="center">
                  <Pagination
                    count={pageCount}
                    page={paginationModel.page + 1}
                    onChange={handlePageChange}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    className="eventCheckinPagination"
                  />
                </Stack>

                <Box className={styles.pageSizeSelector}>
                  <Typography variant="body2" color="text.secondary">
                    Show:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={paginationModel.pageSize}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
                    }}
                    SelectProps={{ native: true }}
                    className={`${styles.pageSizeField} pageSizeField`}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </TextField>
                  <Typography variant="body2" color="text.secondary">
                    per page
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </div>
      </div>
    </main>
  );
};

export default EventCheckinDetail;
